/**
 * Marketing directions on screen (plan Stage 1, 2026-10-09): a colour and a name wherever a phone or an
 * account appears, and the admin's one-time binding of a phone to a direction.
 *
 * **The binding is for ever** — the service refuses another direction later (`direction_is_fixed`), only a
 * database edit changes it — so the dialog says so and holds the button for three seconds. It limits
 * marketing only: the phone still takes sessions and games as before.
 */

import { useState } from 'react'

import type { Device, Direction } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { ConfirmDanger } from '../../ui/ConfirmDanger'

/** Directions by id, refreshed now and then (they change only when an admin adds or renames one). */
export function useDirections() {
  const { api, authEpoch } = useApp()
  const poll = usePoll(() => api.directions(), 60000, [api, authEpoch])
  const byId = new Map((poll.data?.directions ?? []).map((d) => [d.direction_id, d]))
  return { ...poll, byId, list: poll.data?.directions ?? [] }
}

/** The service seeds Ігри/Драми under these names; until an admin renames one, show it in the page's
 *  language. A name an admin chose is shown as typed. */
const SEEDED: Record<string, string> = { games: 'Ігри', dramas: 'Драми' }

export function useDirectionName(): (d: Direction) => string {
  const t = useT()
  return (d) => (SEEDED[d.direction_id] === d.name && t.marketing.builtin[d.direction_id]) || d.name
}

export function DirectionChip({ direction, title }: { direction?: Direction | null; title?: string }) {
  const t = useT()
  const nameOf = useDirectionName()
  if (!direction) return <span className="dir-chip none" title={title ?? t.marketing.noDirectionTitle}>{t.marketing.noDirection}</span>
  return <span className="dir-chip" data-color={direction.color} title={title}>{nameOf(direction)}</span>
}

export function DirectionDot({ direction }: { direction?: Direction | null }) {
  const nameOf = useDirectionName()
  if (!direction) return null
  const name = nameOf(direction)
  return <span className="dir-dot" data-color={direction.color} title={name} aria-label={name} />
}

/** The admin's binding: pick a direction, read that it is for ever, hold, confirm. */
export function BindDirection({ device, directions, onDone, onCancel }: {
  device: Device
  directions: Direction[]
  onDone: () => void
  onCancel: () => void
}) {
  const t = useT()
  const nameOf = useDirectionName()
  const { api } = useApp()
  const [pick, setPick] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const name = device.name || device.model || device.serial
  const chosen = directions.find((d) => d.direction_id === pick)
  return (
    <ConfirmDanger
      title={t.marketing.bindTitle(name)}
      confirmLabel={chosen ? t.marketing.bindConfirm(nameOf(chosen)) : t.marketing.bindPick}
      holdSeconds={3}
      blocked={!chosen}
      onCancel={onCancel}
      onConfirm={() => {
        if (!chosen) return
        api.bindDirection(device.device_id, chosen.direction_id).then(onDone).catch((e) => {
          const d = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
          setProblem(d?.hint ?? (e instanceof Error ? e.message : String(e)))
        })
      }}
    >
      <div>{t.marketing.bindForever}</div>
      <div className="bind-pick" role="group" aria-label={t.marketing.bindPick}>
        {directions.map((d) => (
          <button key={d.direction_id} type="button" className="quiet" data-color={d.color}
                  aria-pressed={pick === d.direction_id} onClick={() => setPick(d.direction_id)}>
            <span className="dir-chip" data-color={d.color}>{nameOf(d)}</span>
          </button>
        ))}
      </div>
      <div className="tiny dim">{t.marketing.bindOnlyMarketing}</div>
      {problem && <div className="tiny bad-text">{problem}</div>}
    </ConfirmDanger>
  )
}

/** The directions the person at the keyboard may upload, push and post in: every one for an admin (or a
 *  service token), else the ones an admin gave them (owner, 2026-10-12). */
export function useMyDirections(): Direction[] {
  const { auth } = useApp()
  const { list } = useDirections()
  if (auth.isAdmin) return list
  const mine = new Set(auth.user?.directions ?? [])
  return list.filter((d) => mine.has(d.direction_id))
}
